/**
 * Regras de qual negocio fica ativo quando o sistema abre.
 *
 * Vivem fora do AuthContext para poderem ser testadas sem React: foi aqui que
 * nasceu o incidente de 16/09/2026, quando "Todos os negocios" virou somente
 * leitura e o padrao antigo tinha gravado essa visao no navegador de todo
 * mundo (ver filialAtiva.test.ts).
 */

export interface FilialAtiva {
    id: string;
    name: string;
}

export interface FilialDisponivel {
    id: string;
    name: string;
    isActive?: boolean;
}

export const VISAO_CONSOLIDADA: FilialAtiva = { id: 'all', name: 'Todos os negócios' };

/** Telas que criam lancamento, e por isso exigem um negocio especifico. */
export const ROTAS_DE_LANCAMENTO = ['/lancar-receita', '/lancar-despesa'];

/**
 * A Matriz: o vinculo mais antigo, que e a ordem em que a API devolve as
 * filiais. Uma filial inativa so e escolhida se nao houver outra.
 */
export function negocioPadrao(filiais: FilialDisponivel[]): FilialAtiva | null {
    const matriz = filiais.find((f) => f.isActive !== false) ?? filiais[0];
    return matriz ? { id: matriz.id, name: matriz.name } : null;
}

/**
 * O que gravar quando a pessoa escolhe "Todos os negocios" no seletor. A marca
 * separa essa escolha do valor antigo, que vinha do padrao sem ninguem pedir.
 */
export function valorSalvoDaVisaoConsolidada(): string {
    return JSON.stringify({ ...VISAO_CONSOLIDADA, escolhaExplicita: true });
}

/**
 * Negocio ativo ao carregar as filiais, a partir do que estava salvo no
 * navegador. `regravar` indica que o valor salvo deve ser trocado por `ativa`.
 */
export function resolverFilialInicial(
    filiais: FilialDisponivel[],
    salvo: string | null,
): { ativa: FilialAtiva | null; regravar: boolean } {
    if (filiais.length === 0) return { ativa: null, regravar: false };

    let parsed: { id?: unknown; escolhaExplicita?: unknown } | null = null;
    try {
        parsed = salvo ? JSON.parse(salvo) : null;
    } catch {
        // Valor corrompido: segue para o padrao em vez de derrubar o carregamento.
        parsed = null;
    }

    // "Todos os negocios" so lanca nada, entao so volta se foi escolha explicita.
    if (parsed?.id === VISAO_CONSOLIDADA.id && parsed?.escolhaExplicita === true) {
        return { ativa: VISAO_CONSOLIDADA, regravar: false };
    }

    const aindaExiste = filiais.find((f) => f.id === parsed?.id);
    if (aindaExiste) {
        return { ativa: { id: aindaExiste.id, name: aindaExiste.name }, regravar: false };
    }

    return { ativa: negocioPadrao(filiais), regravar: true };
}

/**
 * Sem a feature de filiais o seletor nao aparece, entao a visao consolidada
 * seria um estado sem saida. Devolve o negocio para onde ir, ou null quando
 * nao ha nada a corrigir.
 */
export function corrigirVisaoSemFeature(
    ativa: FilialAtiva | null,
    filiais: FilialDisponivel[],
    temFeatureFiliais: boolean,
): FilialAtiva | null {
    if (ativa?.id !== VISAO_CONSOLIDADA.id || temFeatureFiliais) return null;
    return negocioPadrao(filiais);
}

/** Rota que a visao consolidada nao pode abrir. */
export function rotaBloqueadaNaVisao(rota: string, ativaId: string | undefined): boolean {
    return ativaId === VISAO_CONSOLIDADA.id && ROTAS_DE_LANCAMENTO.includes(rota);
}
